import Dexie, { type Table } from 'dexie';
import type {
  Address,
  AppraisalOfferV2,
  AppraisalV2,
  AssignedTask,
  Auction,
  Audit,
  AuditLog,
  Comment,
  Confirmation,
  ConfirmationHistoryEntry,
  CoreDeal,
  Customer,
  CustomerLead,
  EmojiReaction,
  Favorite,
  FileMetadata,
  Hub,
  InstantOfferV2,
  Lender,
  Notification,
  Payment,
  PriceBreakdown,
  ProtectionPlan,
  ProtectionPlanName,
  ProtectionPlanTermLength,
  ProtectionPlanTermMiles,
  ProtectionPlanType,
  Reply,
  Seller,
  SMSAgentMessage,
  Task,
  TaskTemplate,
  TaskV2,
  Trade,
  TradeIn,
  User,
  VehicleFromIndexer,
  VehicleImage,
  VehicleListing,
  VehicleLocation,
} from '../types';

export interface SyncCheck {
  collection: string;
  firstSyncId?: number | null;
  lastSyncId?: number | null;
}

export interface InventorySyncCheck {
  collection: string;
  date_synced: string;
}

class AppDB extends Dexie {
  syncCheck!: Table<SyncCheck, string>;
  inventorySyncCheck!: Table<InventorySyncCheck, string>;

  hubs!: Table<Hub, string>;
  users!: Table<User, string>;
  notifications!: Table<Notification, string>;
  favorites!: Table<Favorite, string>;
  lenders!: Table<Lender, string>;
  auctions!: Table<Auction, string>;
  vehicle_locations!: Table<VehicleLocation, string>;

  core_deals!: Table<CoreDeal, string>;
  cancelled_core_deals!: Table<CoreDeal, string>;

  confirmations!: Table<Confirmation, string>;
  confirmation_history_entries!: Table<ConfirmationHistoryEntry, string>;
  comments!: Table<Comment, string>;
  comment_replies!: Table<Reply, string>;
  deal_files!: Table<FileMetadata, string>;
  deal_payments!: Table<Payment, string>;
  deal_trade_ins!: Table<TradeIn, string>;
  deal_audits!: Table<Audit, string>;
  deal_audit_logs!: Table<AuditLog, string>;

  deal_customers!: Table<Customer, string>;
  deal_customer_addresses!: Table<Address, string>;
  deal_protection_plans!: Table<ProtectionPlan, string>;
  deal_price_breakdowns!: Table<PriceBreakdown, string>;

  deal_tasks!: Table<Task, string>;
  deal_task_assignments!: Table<AssignedTask, string>;

  task_templates!: Table<TaskTemplate, string>;
  tasks!: Table<TaskV2, string>;

  emoji_reactions!: Table<EmojiReaction, string>;

  trades!: Table<Trade, string>;

  vehicle_listings!: Table<VehicleListing, string>;
  vehicle_from_indexer!: Table<VehicleFromIndexer, string>;
  vehicle_listing_images!: Table<VehicleImage, string>;

  sms_agent_messages!: Table<SMSAgentMessage, string>;

  sellers!: Table<Seller, string>;
  protection_plan_names!: Table<ProtectionPlanName, string>;
  protection_plan_types!: Table<ProtectionPlanType, string>;
  protection_plan_term_lengths!: Table<ProtectionPlanTermLength, string>;
  protection_plan_term_miles!: Table<ProtectionPlanTermMiles, string>;
  customer_leads!: Table<CustomerLead, string>;

  // V2 Tables
  appraisals_v2!: Table<AppraisalV2, string>;
  appraisal_offers_v2!: Table<AppraisalOfferV2, string>;
  instant_offers_v2!: Table<InstantOfferV2, string>;

  constructor() {
    super('paddock_local_db');
    this.version(1).stores({
      syncCheck: 'collection',
      inventorySyncCheck: 'collection',

      // v2 stuff
      hubs: 'id',
      users: 'id',
      notifications: 'id, user_id',
      favorites: 'id, user_id, deal_id',
      lenders: 'id, deal_id',
      auctions: 'id',
      vehicle_locations: 'id',

      // v2 deals
      core_deals: 'id, hub_path_identifier, vehicle_listing_id, short_id',

      deal_customers: 'id, deal_id',
      deal_customer_addresses: 'id, customer_id',
      deal_protection_plans: 'id, deal_id',
      deal_price_breakdowns: 'id, deal_id',

      confirmations: 'id, [confirmation_to_type+confirmation_to_id]',
      confirmation_history_entries: 'id, confirmation_id',
      comments: 'id, [commentable_type+commentable_id]',
      comment_replies: 'id, comment_id',
      deal_files: 'id, deal_id',
      deal_payments: 'id, deal_id',
      deal_trade_ins: 'id, deal_id',
      deal_audits: 'id, deal_id',
      deal_audit_logs: 'id, deal_id',

      deal_tasks: 'id, deal_id',
      deal_task_assignments: 'id, deal_id',

      task_templates: 'id',
      tasks: 'id, [taskable_type+taskable_id]',

      emoji_reactions: 'id, [reaction_to_type+reaction_to_id]',

      trades: 'id',

      vehicle_listings: 'id, vehicle_id',
      vehicle_listing_images: 'id, vehicle_listing_id',
      vehicle_from_indexer: 'id, vin',

      sms_agent_messages: 'id, deal_id',

      // appraisal stuff
      appraisals: 'id',
      appraisal_offers: 'id, [appraisal_id]',
      sellers: 'id',
      protection_plan_names: 'id',
      protection_plan_types: 'id',
      protection_plan_term_lengths: 'id',
      protection_plan_term_miles: 'id',
      customer_leads: 'id',
      instant_offers: 'id, [channel_id]',

      // V2 Tables
      appraisals_v2: 'id, tracking_id, created_at',
      appraisal_offers_v2: 'id, tracking_id, appraisal_v2_id, created_at',
      instant_offers_v2: 'id, tracking_id, channel_id',
    });
    this.version(2).stores({
      cancelled_core_deals:
        'id, hub_path_identifier, vehicle_listing_id, short_id',
    });
  }
}

export const db = new AppDB();
